import {
  Button,
  createStyles,
  Grid,
  Paper,
  Typography,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import { Show } from "features/shared/components";
import React, { useState } from "react";
import {
  activateCauseOfDeath,
  createCauseOfDeath,
  deleteCauseOfDeath,
  updateCauseOfDeath,
} from "redux/operations";
import { CauseOfDeath } from "shared/types";
import type { CauseOfDeathFormState } from "../causeOfDeath.types";
import { useFilterForm } from "../hooks/useFilterForm";
import CauseOfDeathFilterForm from "./causeOfDeathFilterForm";
import CauseOfDeathForm from "./causeOfDeathForm";
import CauseOfDeathListTable from "./causeOfDeathListTable";

type CauseOfDeathPageProps = {
  activateCauseOfDeath: (
    ...params: Parameters<typeof activateCauseOfDeath>
  ) => Promise<void>;
  createCauseOfDeath: (
    ...params: Parameters<typeof createCauseOfDeath>
  ) => Promise<void>;
  deleteCauseOfDeath: (
    ...params: Parameters<typeof deleteCauseOfDeath>
  ) => Promise<void>;
  farmCausesOfDeath: CauseOfDeath[];
  updateCauseOfDeath: (
    ...params: Parameters<typeof updateCauseOfDeath>
  ) => Promise<void>;
} & WithStyles<typeof styles>;

function CauseOfDeathPage({
  activateCauseOfDeath,
  classes,
  createCauseOfDeath,
  deleteCauseOfDeath,
  farmCausesOfDeath,
  updateCauseOfDeath,
}: CauseOfDeathPageProps) {
  const { t } = useTranslations();

  const [isFormPanelOpen, setIsFormPanelOpen] = useState<boolean>(false);
  const [selectedCauseOfDeath, setSelectedCauseOfDeath] =
    useState<CauseOfDeath | null>(null);

  const { filterForm, handleChangeFilter } = useFilterForm();

  const toggleFormPanel = (isOpen: boolean) => {
    setIsFormPanelOpen(isOpen);
    if (isOpen) {
      return;
    }
    setSelectedCauseOfDeath(null);
  };

  const handleCloseFormPanel = () => {
    toggleFormPanel(false);
  };

  const handleOpenNewCauseOfDeathPanel = () => {
    setSelectedCauseOfDeath(null);
    toggleFormPanel(true);
  };

  const handleSubmitForm = async (formState: CauseOfDeathFormState) => {
    if (selectedCauseOfDeath != null) {
      await updateCauseOfDeath(selectedCauseOfDeath.id, {
        name: formState.name.value,
      });
      toggleFormPanel(false);
      return;
    }

    await createCauseOfDeath({ name: formState.name.value });

    toggleFormPanel(false);
  };

  const handleEditCauseOfDeath = (row: CauseOfDeath) => {
    setSelectedCauseOfDeath(row);
    toggleFormPanel(true);
  };

  const handleToggleStatus = (row: CauseOfDeath, isCurrentDeleted: boolean) => {
    if (isCurrentDeleted) {
      activateCauseOfDeath(row.id, { name: row.name });
      return;
    }

    deleteCauseOfDeath(row.id);
  };

  return (
    <Paper className={classes.root} square={true}>
      <Grid item sm={12} xs={12} className={classes.grid}>
        <Typography className={classes.header} gutterBottom={true}>
          {t("livestock:cause-of-animals-deaths")}
        </Typography>
        <CauseOfDeathFilterForm
          filterForm={filterForm}
          onChangeFilter={handleChangeFilter}
        />

        <Show
          when={isFormPanelOpen}
          fallback={
            <AddNewButton
              classes={classes}
              onClick={handleOpenNewCauseOfDeathPanel}
            />
          }
        >
          <CauseOfDeathForm
            causesOfDeath={farmCausesOfDeath}
            onCancel={handleCloseFormPanel}
            onSubmit={handleSubmitForm}
            selectedCauseOfDeath={selectedCauseOfDeath}
          />
        </Show>

        <CauseOfDeathListTable
          causesOfDeath={farmCausesOfDeath}
          filterForm={filterForm}
          isFormPanelOpen={isFormPanelOpen}
          onEditClick={handleEditCauseOfDeath}
          onToggleStatus={handleToggleStatus}
        />
      </Grid>
    </Paper>
  );
}

type AddNewButtonProps = {
  onClick: () => void;
} & WithStyles<typeof styles>;

function AddNewButton({ classes, onClick }: AddNewButtonProps) {
  const { t } = useTranslations();

  return (
    <Button
      className={classes.addNewButton}
      color="primary"
      data-testid="add-new-cause-of-death-button"
      onClick={onClick}
      variant="contained"
    >
      {t("livestock:new-cause")}
    </Button>
  );
}

const styles = () =>
  createStyles({
    root: {
      bottom: 0,
      height: "100%",
      minHeight: "calc(100vh - 90px)",
      overflowX: "auto",
      padding: "46px",
      top: "17%",
      width: "100%",
      zIndex: -1,
    },
    grid: {
      backgroundColor: "#fff",
      flexGrow: 1,
      height: "100%",
      width: "100%",
    },
    header: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
    },
    addNewButton: {
      borderRadius: "18px",
      float: "right",
      marginBottom: "16px",
      marginRight: 0,
      marginTop: "34px",
      width: "153px",
    },
  });

export default withStyles(styles)(CauseOfDeathPage);
